import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowLeft, BarChart3, BookOpen, CircleHelp, Contact, Copy, Hammer, Settings, ShieldCheck, SquarePen, UserPlus, Users, X } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNotification } from "@/contexts/NotificationContext";
import LoadingOverlay from "@/components/overlays/LoadingOverlay";
import { NotificationModal, SiteTip } from "@/components/modals";
import CourseEnrollDialog, { type EnrollTarget } from "@/components/courses/CourseEnrollDialog";
import PermissionKitEditor, {
  clonePermissions,
  fingerprint,
  type CatalogCapability,
  type CatalogPage,
  type RoleFlagOption,
  type RolePermissions,
} from "@/components/roles/PermissionKitEditor";
import { publishSchoolPageNav } from "@/hooks/useSchoolPageNav";
import useFloatRowInView, { floatRowStyle } from "@/hooks/useFloatRowInView";
import { autoOpenSchoolId } from "@/utils/sessionBadges";
import { api, del, errorMessage, post, put } from "@/lib/api";
import { accessDeniedNavigateState } from "@/utils/accessDenied";
import { loginFieldLocked } from "@/utils/loginContactLock";
import { ContactFields } from "@/components/OwnContactForm";
import "@/components/OwnContactForm.css";
import SchoolYearPanel from "@/components/schools/SchoolYearPanel";
import SchoolPerformance from "@/components/schools/SchoolPerformance";
import SchoolAnnouncements from "@/components/schools/SchoolAnnouncements";
import "./Schools.css";
import "./SchoolManage.css";

const ADD_PERSON_DIALOG_STYLE = { width: "min(34rem, 100%)" } as const;
const ADD_KIT_DIALOG_STYLE = { width: "min(44rem, 100%)", maxHeight: "min(90vh, 52rem)" } as const;
const CONTACT_GRANT_DIALOG_STYLE = { width: "min(28rem, 100%)" } as const;
const CONTACT_DIALOG_STYLE = { width: "min(26rem, 100%)" } as const;

type ContactLevel = "none" | "view" | "update";

interface ContactGrants {
  ugpId: string | null;
  student: ContactLevel;
  guardian: ContactLevel;
  team: { student: ContactLevel; guardian: ContactLevel };
}

interface SchoolRow {
  id: string;
  name: string;
  description?: string | null;
  display_label?: string | null;
}

interface SchoolCourse {
  id: string;
  title: string;
  description: string | null;
  coverImageUrl: string | null;
  assignmentId: string;
  sourceCourseId: string;
  canEdit: boolean;
  canCopy?: boolean;
  canPropose?: boolean;
  canEnroll?: boolean;
  modLock?: boolean;
  canBuild?: boolean;
  isMaster?: boolean;
  primaryTaxonomy?: { id: string; title: string | null; type: string | null } | null;
}

interface SchoolParent {
  userId: string;
  name: string;
  students: string[];
}

interface PeopleLink {
  relationshipId: string | null;
  kind: "guardian" | "counselor";
  userId: string | null;
  name: string;
  label: string;
  primary: boolean;
  emergency?: boolean;
  guardianType?: string | null;
  email?: string | null;
  platformAccess?: boolean;
}

const GUARDIAN_TYPE_OPTIONS = [
  { value: "father", label: "Father" },
  { value: "mother", label: "Mother" },
  { value: "grandfather", label: "Grandfather" },
  { value: "grandmother", label: "Grandmother" },
  { value: "step_father", label: "Step Father" },
  { value: "step_mother", label: "Step Mother" },
  { value: "foster_parent", label: "Foster Parent" },
  { value: "other", label: "Other" },
] as const;

interface ParentSearchHit {
  id: string;
  ugpId?: string | null;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  can_login?: boolean;
  seat?: string | null;
}

interface Participant {
  userId: string;
  ugpId?: string | null;
  email?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  teamName?: string;
  role: string;
  roleLabel?: string;
  period_title?: string | null;
  groups_periods_id?: string | null;
}

const ROLE_OPTIONS = [
  { key: "super_admin", flag: "superAdmin", label: "Super Admin" },
  { key: "admin", flag: "admin", label: "Admin" },
  { key: "teacher", flag: "teacher", label: "Teacher" },
  { key: "student", flag: "student", label: "Student" },
  { key: "curriculum_developer", flag: "curriculumDeveloper", label: "Curriculum Developer" },
  { key: "counselor", flag: "counselor", label: "Counselor" },
] as const;

interface KitKeyRow {
  id: string;
  label: string;
  kind: "page" | "capability";
}

interface RoleKitPlan {
  needsConfirmation: true;
  mode: "create_gp" | "join_gp" | "ugp_delta";
  teamName: string;
  message: string;
  gp: KitKeyRow[];
  ugpAllow: KitKeyRow[];
  ugpDeny: KitKeyRow[];
}

type KitConfirmAction =
  | { kind: "add" }
  | { kind: "link"; hit?: ParentSearchHit }
  | { kind: "grant"; link: PeopleLink };

interface AddKitPayload {
  rosterKey: string;
  recipeKey: string;
  label: string;
  source: "saved" | "template";
  note?: string;
  catalog: {
    pages: CatalogPage[];
    capabilities: CatalogCapability[];
    roleFlags: RoleFlagOption[];
  };
  permissions: RolePermissions;
}

function displayName(p: Participant) {
  const name = [p.first_name, p.last_name].filter(Boolean).join(" ").trim();
  return name || p.email || "Unnamed";
}

function ContactDoor({
  title,
  detail,
  value,
  team,
  disabled,
  onChange,
}: {
  title: string;
  detail: string;
  value: ContactLevel;
  team: ContactLevel;
  disabled?: boolean;
  onChange: (next: ContactLevel) => void;
}) {
  const updateLocked = team === "update";
  const viewFromTeam = team === "view";
  const updateChecked = value === "update" || updateLocked;
  const viewChecked = !updateChecked && (value === "view" || viewFromTeam);
  const viewLocked = updateChecked || viewFromTeam || updateLocked;
  return (
    <fieldset className="al-contact-grant" disabled={disabled} aria-label={title}>
      <p className="al-contact-grant__title">{title}</p>
      <p className="al-contact-grant__detail">{detail}</p>
      <div className="al-contact-grant__checks">
        <label className="al-contact-grant__choice">
          <span className="al-school-manage-check">
            <input
              type="checkbox"
              checked={updateChecked}
              disabled={disabled || updateLocked}
              onChange={(event) => {
                if (updateLocked) return;
                onChange(event.target.checked ? "update" : "none");
              }}
            />
            Update
          </span>
          {updateLocked ? <span className="al-contact-grant__note">On the Parents team</span> : null}
        </label>
        <label className="al-contact-grant__choice">
          <span className="al-school-manage-check">
            <input
              type="checkbox"
              checked={viewChecked}
              disabled={disabled || viewLocked}
              onChange={(event) => {
                if (viewLocked) return;
                onChange(event.target.checked ? "view" : "none");
              }}
            />
            View
          </span>
          {updateChecked ? (
            <span className="al-contact-grant__note">Included with update</span>
          ) : team === "view" ? (
            <span className="al-contact-grant__note">On the Parents team</span>
          ) : null}
        </label>
      </div>
    </fieldset>
  );
}

function loginStatusHint(
  link: { email?: string | null; kind: string },
  canGrant: boolean,
  canEditContact: boolean,
) {
  if (!link.email) {
    if (canEditContact && canGrant) {
      return "Add an email with the contact button. Give access appears after the email is saved.";
    }
    if (canEditContact) {
      return "Add an email with the contact button. Ask an admin or support to give the sign-in.";
    }
    return "They need an email before they can sign in. Ask an admin or support.";
  }
  if (canGrant) return "Use the Give access button on this row.";
  return "Ask an admin or support to give this sign-in.";
}

function RelationshipButtonHelp() {
  return (
    <ul className="al-people-help">
      <li>
        <span className="al-people-help-icon" aria-hidden="true">
          <Contact size={14} />
        </span>
        <p>
          <strong>Contact</strong>
          Opens email, phone, and address. View Contact shows this button. Update Contact is what changes those fields.
          A filled sign-in email or phone also needs Edit User Data, or Edit User Data Master when that person has a
          context outside this school. The platform owner’s email and phone can only be changed by that person.
          Emergency contact is set on the relationship, not here. Update Contact does not edit the relationship.
        </p>
      </li>
      <li>
        <span className="al-people-help-icon" aria-hidden="true">
          <Settings size={14} />
        </span>
        <p>
          <strong>Contact permissions</strong>
          On a guardian row. Grant Contact Permissions sets that guardian’s view or update doors for the student and for
          other guardians. It does not add or change the relationship. Counselor rows do not show this.
        </p>
      </li>
      <li>
        <span className="al-people-help-icon" aria-hidden="true">
          <SquarePen size={14} />
        </span>
        <p>
          <strong>Edit relationship</strong>
          Changes a guardian’s type, primary, and emergency. Person Links is required, and guardians must be an allowed
          link type. Counselor rows never show this. Clicking a guardian row opens the same edit. Person Links does not
          change contact or grant a sign-in.
        </p>
      </li>
      <li>
        <span className="al-people-help-icon" aria-hidden="true">
          <ShieldCheck size={14} />
        </span>
        <p>
          <strong>Give access</strong>
          Gives this person a sign-in. Add Users for the parent role, or Add Users for the counselor role on a counselor
          row. It appears only when they have an email and cannot sign in yet.
        </p>
      </li>
      <li>
        <span className="al-people-help-icon al-people-help-icon--remove" aria-hidden="true">
          <X size={11} />
        </span>
        <p>
          <strong>Remove</strong>
          Removes the relationship. Double-click it. One click does nothing. Person Links is required.
        </p>
      </li>
      <li>
        <span className="al-people-help-icon" aria-hidden="true">
          <UserPlus size={14} />
        </span>
        <p>
          <strong>Add a Relationship</strong>
          The button at the bottom of the list. Person Links is required. View Person Links opens this list and does not
          add, edit, or remove anyone.
        </p>
      </li>
    </ul>
  );
}

function InlineLoading({ message }: { message: string }) {
  return (
    <p className="al-school-manage-loading" role="status">
      <span className="al-school-manage-spinner" aria-hidden="true" />
      {message}
    </p>
  );
}

function isRoleKitPlan(data: unknown): data is RoleKitPlan {
  return Boolean(
    data &&
      typeof data === "object" &&
      !Array.isArray(data) &&
      (data as RoleKitPlan).needsConfirmation,
  );
}

function KitPlanList({ title, hint, rows }: { title: string; hint: string; rows: KitKeyRow[] }) {
  if (!rows.length) return null;
  return (
    <div className="al-student-plan">
      <p className="al-student-plan-title">{title}</p>
      <p className="al-student-plan-hint">{hint}</p>
      <ul>
        {rows.map((row) => (
          <li key={`${row.kind}:${row.id}`}>{row.label}</li>
        ))}
      </ul>
    </div>
  );
}

function kitPlanHints(plan: RoleKitPlan) {
  const group = plan.teamName || "group";
  if (plan.mode === "create_gp") {
    return {
      gp: `OK stores these on the ${group} group. Everyone in that group receives them.`,
      allow: "",
      deny: "",
    };
  }
  if (plan.mode === "join_gp") {
    return {
      gp: `These already match the ${group} group. This person receives them by joining.`,
      allow: "",
      deny: "",
    };
  }
  return {
    gp: `Already on the ${group} group. This person receives them by joining.`,
    allow: "In this kit, and not on the group yet. Stored on this membership only, so the shared group stays as it is.",
    deny: "The group grants these. This kit leaves them off, so this membership is denied them.",
  };
}

export default function SchoolManage() {
  const { schoolId } = useParams();
  const { capabilities, addUserRoles, viewUserRoles, pageAccess, session, loading: authLoading } = useAuth();
  const { toast, notice } = useNotification();
  const canEdit = Boolean(capabilities?.editSchools);
  const canEditCourses = Boolean(capabilities?.editCourses);
  const canCopyCourses = Boolean(capabilities?.copyCourses);
  const canAdd = Boolean(capabilities?.addUsers);
  const canOverrideAddPermissions = Boolean(capabilities?.overrideAddUserPermissions);
  const canEditGradeScale = Boolean(capabilities?.editGradeScale);
  const canEditCalendar = Boolean(capabilities?.editSchoolCalendar);
  const canViewPersonLinks = Boolean(capabilities?.viewPersonLinks || capabilities?.personLinks);
  const canSeeContact = Boolean(capabilities?.viewContact || capabilities?.updateContact);
  const canEditContact = Boolean(capabilities?.updateContact);
  const canLinkParents = Boolean(capabilities?.personLinks);
  const canSearchPeople = Boolean(capabilities?.searchPeople);
  const canGrantContact = Boolean(capabilities?.grantContactPermissions);
  const linkPersonTypes = session?.acuityLearn?.linkPersonTypes;
  const canLinkGuardians =
    canLinkParents &&
    (linkPersonTypes == null ||
      Boolean(linkPersonTypes.parents) ||
      Boolean(linkPersonTypes.guardians));
  const canLinkCounselors =
    canLinkParents && (linkPersonTypes == null || linkPersonTypes.counselors !== false);
  const canGrantParentAccess = Boolean(addUserRoles?.parent);
  const canGrantCounselorAccess = Boolean(addUserRoles?.counselor);
  const canSeeCatalog = Boolean(
    pageAccess?.courses ||
      capabilities?.assignCoursesToSchools ||
      capabilities?.copyCourses ||
      capabilities?.setSchoolCopyEdit ||
      capabilities?.createCourses ||
      capabilities?.createMasterCourses ||
      capabilities?.editCourses ||
      capabilities?.removeCourses,
  );
  const canSeeCourses = Boolean(capabilities?.viewCourses || canSeeCatalog);
  const canSeePeople = Boolean(capabilities?.viewUsers);
  const canOpenBuilder = Boolean(pageAccess?.["course-builder"]);
  /** Enroll/revoke: course.ManageEnrollments + View People (roster picker). */
  const canManageEnrollments = Boolean(capabilities?.manageEnrollments && canSeePeople);
  const canOpen = Boolean(
    pageAccess?.schools ||
      canEdit ||
      canAdd ||
      canSeePeople ||
      capabilities?.createSchools ||
      capabilities?.removeSchools,
  );

  const allowedRoles = useMemo(
    () => ROLE_OPTIONS.filter((opt) => opt.key !== "super_admin" && Boolean(addUserRoles?.[opt.flag])),
    [addUserRoles],
  );
  const visibleRosterRoles = useMemo(
    () => ROLE_OPTIONS.filter((opt) => Boolean(viewUserRoles?.[opt.flag])),
    [viewUserRoles],
  );

  const [school, setSchool] = useState<SchoolRow | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [participants, setParticipants] = useState<Participant[]>([]);
  const [schoolParents, setSchoolParents] = useState<SchoolParent[]>([]);
  const [participantsLoading, setParticipantsLoading] = useState(true);
  const [courses, setCourses] = useState<SchoolCourse[]>([]);
  const [coursesLoading, setCoursesLoading] = useState(true);
  const [rosterFilter, setRosterFilter] = useState("all");
  const [showAdd, setShowAdd] = useState(false);
  const [newFirstName, setNewFirstName] = useState("");
  const [newLastName, setNewLastName] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState("student");
  const [showKit, setShowKit] = useState(false);
  const [kit, setKit] = useState<AddKitPayload | null>(null);
  const [kitDraft, setKitDraft] = useState<RolePermissions | null>(null);
  const [kitQuery, setKitQuery] = useState("");
  const [kitLoading, setKitLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [rolePlan, setRolePlan] = useState<RoleKitPlan | null>(null);
  const [kitAction, setKitAction] = useState<KitConfirmAction | null>(null);
  const [enrollTarget, setEnrollTarget] = useState<EnrollTarget | null>(null);
  const [courseSaving, setCourseSaving] = useState(false);
  const [navSection, setNavSection] = useState<string | null>("details");
  const [editingField, setEditingField] = useState<"name" | "description" | null>(null);
  const [showHelp, setShowHelp] = useState(false);
  const [relationshipHelp, setRelationshipHelp] = useState(false);
  const [parentStudent, setParentStudent] = useState<Participant | null>(null);
  const [parentLinks, setParentLinks] = useState<PeopleLink[]>([]);
  const [parentLinksLoading, setParentLinksLoading] = useState(false);
  const [parentQuery, setParentQuery] = useState("");
  const [parentHits, setParentHits] = useState<ParentSearchHit[]>([]);
  const [parentSearched, setParentSearched] = useState(false);
  const [parentBusy, setParentBusy] = useState(false);
  const [parentError, setParentError] = useState("");
  const [peopleLabel, setPeopleLabel] = useState("Support & Family");
  const [peopleAdding, setPeopleAdding] = useState(false);
  const [editingLink, setEditingLink] = useState<PeopleLink | null>(null);
  const [contactOpen, setContactOpen] = useState(false);
  const [contactGrantLink, setContactGrantLink] = useState<PeopleLink | null>(null);
  const [personContact, setPersonContact] = useState<{ userId: string; name: string } | null>(null);
  const [personContactValues, setPersonContactValues] = useState({
    email: "",
    phone: "",
    street: "",
    city: "",
    state: "",
    zip: "",
  });
  const [personContactRecord, setPersonContactRecord] = useState<{
    email: string;
    phone: string;
    canLogin: boolean;
    emailEditable?: boolean;
    phoneEditable?: boolean;
    loginLock?: "owner" | "edit" | "scope" | null;
    street: string;
    city: string;
    state: string;
    zip: string;
  } | null>(null);
  const [personContactLoading, setPersonContactLoading] = useState(false);
  const [personContactError, setPersonContactError] = useState("");
  const [personContactSaving, setPersonContactSaving] = useState(false);
  const [personContactSaved, setPersonContactSaved] = useState("");
  const [contactLoading, setContactLoading] = useState(false);
  const [contactBusy, setContactBusy] = useState(false);
  const [contactError, setContactError] = useState("");
  const [contactGrants, setContactGrants] = useState<ContactGrants | null>(null);
  const [contactDraft, setContactDraft] = useState<{ student: ContactLevel; guardian: ContactLevel } | null>(null);
  const [selectedHit, setSelectedHit] = useState<ParentSearchHit | null>(null);
  const [peopleSearching, setPeopleSearching] = useState(false);
  const [peopleSaving, setPeopleSaving] = useState(false);
  const [linkKind, setLinkKind] = useState<"guardian" | "counselor">("guardian");
  const [linkMode, setLinkMode] = useState<"search" | "create">("create");
  const [guardianType, setGuardianType] = useState("father");
  const [linkPrimary, setLinkPrimary] = useState(false);
  const [linkEmergency, setLinkEmergency] = useState(false);
  const [createFirst, setCreateFirst] = useState("");
  const [createLast, setCreateLast] = useState("");
  const [createEmail, setCreateEmail] = useState("");
  const pageRef = useRef<HTMLDivElement | null>(null);
  const peopleRef = useRef<HTMLElement | null>(null);
  const addPersonSlotRef = useRef<HTMLLIElement | null>(null);
  const pinAddPerson = useFloatRowInView(
    peopleRef,
    addPersonSlotRef,
    canAdd,
    `${participants.length}:${schoolParents.length}:${rosterFilter}`,
  );

  const schoolSections = useMemo(() => {
    const rows: { key: string; name: string }[] = [{ key: "details", name: "Details" }];
    if (canSeeCourses) rows.push({ key: "courses", name: "Courses" });
    rows.push({ key: "performance", name: "Performance" });
    if (canSeePeople || canAdd) rows.push({ key: "people", name: "Participants" });
    if (canEditGradeScale) rows.push({ key: "grades", name: "Grades" });
    if (canEditCalendar) rows.push({ key: "calendar", name: "Calendar" });
    return rows;
  }, [canSeeCourses, canSeePeople, canAdd, canEditGradeScale, canEditCalendar]);

  const scrollToSection = useCallback((key: string) => {
    setNavSection(key);
    const heading = pageRef.current?.querySelector(`#al-school-${key}`);
    const scroller = pageRef.current?.closest(".al-shell-body");
    if (!(heading instanceof HTMLElement) || !(scroller instanceof HTMLElement)) return;
    const top =
      heading.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
    scroller.scrollTop = Math.max(0, top - 8);
  }, []);

  useEffect(() => {
    if (allowedRoles.length && !allowedRoles.some((r) => r.key === newRole)) {
      setNewRole(allowedRoles[0].key);
    }
  }, [allowedRoles, newRole]);

  const loadKit = useCallback(
    async (roleKey: string) => {
      if (!schoolId || !canAdd) {
        setKit(null);
        setKitDraft(null);
        return;
      }
      setKitLoading(true);
      setKit(null);
      setKitDraft(null);
      try {
        const envelope = await api<{ success: boolean; data: AddKitPayload }>(
          `/api/schools/${encodeURIComponent(schoolId)}/participants/kit?role=${encodeURIComponent(roleKey)}`,
        );
        const data = envelope.data;
        setKit(data);
        setKitDraft(clonePermissions(data.permissions));
        setKitQuery("");
      } catch (err) {
        setKit(null);
        setKitDraft(null);
        notice({
          type: "error",
          title: "Could not load role defaults",
          subtext: errorMessage(err),
          duration: 6000,
        });
      } finally {
        setKitLoading(false);
      }
    },
    [schoolId, canAdd, notice],
  );

  useEffect(() => {
    if (!showAdd || !canAdd || !schoolId) return;
    void loadKit(newRole);
  }, [showAdd, canAdd, schoolId, newRole, loadKit]);

  const loadParticipants = useCallback(async () => {
    if (!schoolId || !canSeePeople) {
      setParticipants([]);
      setSchoolParents([]);
      setParticipantsLoading(false);
      return;
    }
    setParticipantsLoading(true);
    try {
      const envelope = await api<{ success: boolean; data?: { participants?: Participant[] } }>(
        `/api/schools/${encodeURIComponent(schoolId)}/participants`,
      );
      setParticipants(Array.isArray(envelope.data?.participants) ? envelope.data.participants : []);
      if (!canLinkParents) {
        setSchoolParents([]);
      } else {
        try {
          const parentsEnv = await api<{ success: boolean; data?: SchoolParent[] }>(
            `/api/schools/${encodeURIComponent(schoolId)}/parent-links`,
          );
          setSchoolParents(Array.isArray(parentsEnv.data) ? parentsEnv.data : []);
        } catch {
          setSchoolParents([]);
        }
      }
    } catch (err) {
      setParticipants([]);
      setSchoolParents([]);
      notice({
        type: "error",
        title: "Could not load participants",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setParticipantsLoading(false);
    }
  }, [schoolId, canSeePeople, canLinkParents, notice]);

  const loadCourses = useCallback(async () => {
    if (!schoolId || !canSeeCourses) return;
    setCoursesLoading(true);
    try {
      const envelope = await api<{ success: boolean; data?: { courses?: SchoolCourse[] } }>(
        `/api/courses/schools/${encodeURIComponent(schoolId)}`,
      );
      setCourses(Array.isArray(envelope.data?.courses) ? envelope.data.courses : []);
    } catch (err) {
      setCourses([]);
      notice({
        type: "error",
        title: "Could not load courses",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setCoursesLoading(false);
    }
  }, [schoolId, canSeeCourses, notice]);

  const load = useCallback(async () => {
    if (!schoolId) return;
    setLoading(true);
    try {
      const envelope = await api<{ success: boolean; data: SchoolRow }>(
        `/api/schools/${encodeURIComponent(schoolId)}`,
      );
      const row = envelope.data;
      setSchool(row);
      setName(row?.name || "");
      setDescription(row?.description || "");
    } catch (err) {
      setSchool(null);
      notice({
        type: "error",
        title: "Could not load school",
        subtext: errorMessage(err),
        duration: 6000,
      });
    } finally {
      setLoading(false);
    }
  }, [schoolId, notice]);

  useEffect(() => {
    if (!authLoading && canOpen) {
      void load();
      void loadParticipants();
      void loadCourses();
    }
  }, [authLoading, canOpen, load, loadParticipants, loadCourses]);

  const byRole = useMemo(() => {
    const groups: Record<string, Participant[]> = {};
    for (const opt of ROLE_OPTIONS) groups[opt.key] = [];
    for (const p of participants) {
      const key = p.role || "student";
      if (!groups[key]) groups[key] = [];
      groups[key].push(p);
    }
    return groups;
  }, [participants]);

  useEffect(() => {
    if (authLoading || !canOpen || !school) {
      publishSchoolPageNav(null);
      return () => publishSchoolPageNav(null);
    }
    publishSchoolPageNav({
      schoolId: school.id,
      schoolName: school.name,
      sections: schoolSections,
      sectionId: navSection,
      pick: scrollToSection,
    });
    return () => publishSchoolPageNav(null);
  }, [authLoading, canOpen, school, schoolSections, navSection, scrollToSection]);

  useEffect(() => {
    if (!school) return undefined;
    const scroller = pageRef.current?.closest(".al-shell-body");
    if (!(scroller instanceof HTMLElement)) return undefined;
    const nodes = schoolSections
      .map((section) => pageRef.current?.querySelector(`#al-school-${section.key}`))
      .filter((node): node is HTMLElement => node instanceof HTMLElement);
    if (!nodes.length) return undefined;
    const io = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const id = visible[0]?.target.id.replace(/^al-school-/, "");
        if (id) setNavSection(id);
      },
      { root: scroller, rootMargin: "-8% 0px -68% 0px", threshold: 0.05 },
    );
    nodes.forEach((node) => io.observe(node));
    return () => io.disconnect();
  }, [school, schoolSections, coursesLoading, participantsLoading]);

  if (authLoading) {
    return <LoadingOverlay isVisible message="Loading…" />;
  }

  if (!canOpen) {
    return <Navigate to="/" replace state={accessDeniedNavigateState("schools")} />;
  }

  const handleSaveSchool = async (nextName = name, nextDescription = description) => {
    if (!schoolId) return;
    const trimmedName = nextName.trim();
    if (!trimmedName) {
      setName(school?.name || "");
      setEditingField(null);
      return;
    }
    if (
      trimmedName === (school?.name || "") &&
      (nextDescription.trim() || "") === (school?.description || "")
    ) {
      setEditingField(null);
      return;
    }
    setSaving(true);
    try {
      const envelope = await put<{ success: boolean; data: SchoolRow }>(
        `/api/schools/${encodeURIComponent(schoolId)}`,
        {
          name: trimmedName,
          description: nextDescription.trim() || null,
          display_label: "School",
        },
      );
      setSchool(envelope.data);
      setName(envelope.data.name || "");
      setDescription(envelope.data.description || "");
      setEditingField(null);
      toast.success("School updated");
    } catch (err) {
      notice({
        type: "error",
        title: "Could not update school",
        subtext: errorMessage(err),
        duration: 7000,
      });
    } finally {
      setSaving(false);
    }
  };

  const resetLinkDraft = () => {
    setParentQuery("");
    setParentHits([]);
    setParentSearched(false);
    setParentError("");
    setLinkMode("create");
    setGuardianType("father");
    setLinkPrimary(false);
    setLinkEmergency(false);
    setCreateFirst("");
    setCreateLast("");
    setCreateEmail("");
    setSelectedHit(null);
  };

  const closeParents = () => {
    if (parentBusy) return;
    setParentStudent(null);
    setParentLinks([]);
    setLinkKind("guardian");
    setPeopleAdding(false);
    setEditingLink(null);
    setRelationshipHelp(false);
    setContactOpen(false);
    setContactGrantLink(null);
    resetLinkDraft();
  };

  const openParents = async (person: Participant) => {
    if (!schoolId) return;
    setParentStudent(person);
    setParentLinks([]);
    setLinkKind("guardian");
    setPeopleAdding(false);
    resetLinkDraft();
    setParentLinksLoading(true);
    try {
      const env = await api<{ success: boolean; data: PeopleLink[]; containerLabel?: string }>(
        `/api/schools/${encodeURIComponent(schoolId)}/person-links?studentUserId=${encodeURIComponent(person.userId)}`,
      );
      setParentLinks(env.data || []);
      if (env.containerLabel) setPeopleLabel(env.containerLabel);
    } catch (err) {
      setParentError(errorMessage(err));
    } finally {
      setParentLinksLoading(false);
    }
  };

  const searchParents = async (event: FormEvent) => {
    event.preventDefault();
    if (!schoolId) return;
    const q = parentQuery.trim();
    if (!q) return;
    setParentBusy(true);
    setPeopleSearching(true);
    setParentError("");
    setSelectedHit(null);
    try {
      const params = new URLSearchParams({ q });
      if (linkKind === "counselor" && parentStudent?.groups_periods_id) {
        params.set("kind", "counselor");
        params.set("groupsPeriodsId", parentStudent.groups_periods_id);
      }
      const env = await api<{ success: boolean; data: ParentSearchHit[] }>(
        `/api/schools/${encodeURIComponent(schoolId)}/participants/search?${params.toString()}`,
      );
      setParentHits(env.data || []);
      setParentSearched(true);
    } catch (err) {
      setParentError(errorMessage(err));
    } finally {
      setParentBusy(false);
      setPeopleSearching(false);
    }
  };

  const linkPersonToStudent = async (hit?: ParentSearchHit, confirm = false) => {
    if (!schoolId || !parentStudent) return;
    if (hit && String(hit.id) === String(parentStudent.userId)) {
      setParentError("A student cannot be linked to themselves.");
      return;
    }
    const creatingParent = linkKind === "guardian" && linkMode === "create" && !hit;
    setParentBusy(true);
    setPeopleSaving(true);
    setParentError("");
    try {
      const env = await post<{
        success: boolean;
        data?: PeopleLink[] | RoleKitPlan;
        containerLabel?: string;
      }>(`/api/schools/${encodeURIComponent(schoolId)}/person-links`, {
        studentUserId: parentStudent.userId,
        kind: linkKind,
        mode: linkKind === "counselor" ? "search" : linkMode,
        ...(creatingParent ? { confirm } : {}),
        ...(hit ? { userId: hit.id, ...(hit.ugpId ? { ugpId: hit.ugpId } : {}) } : {}),
        ...(linkKind === "guardian" && linkMode === "create"
          ? {
              firstName: createFirst.trim(),
              lastName: createLast.trim(),
              email: createEmail.trim(),
              guardianType,
              primary: linkPrimary,
              emergency: linkEmergency,
            }
          : {}),
        ...(linkKind === "guardian" && hit ? { guardianType, primary: linkPrimary, emergency: linkEmergency } : {}),
      });
      if (isRoleKitPlan(env.data)) {
        setRolePlan(env.data);
        setKitAction({ kind: "link", hit });
        return;
      }
      setParentLinks(Array.isArray(env.data) ? env.data : []);
      if (env.containerLabel) setPeopleLabel(env.containerLabel);
      setPeopleAdding(false);
      setRolePlan(null);
      setKitAction(null);
      resetLinkDraft();
      await loadParticipants();
    } catch (err) {
      setParentError(errorMessage(err));
    } finally {
      setParentBusy(false);
      setPeopleSaving(false);
    }
  };

  const openEditLink = (link: PeopleLink) => {
    if (link.kind !== "guardian") return;
    setPeopleAdding(false);
    setEditingLink(link);
    setGuardianType(link.guardianType || "other");
    setLinkPrimary(Boolean(link.primary));
    setLinkEmergency(Boolean(link.emergency));
    setParentError("");
  };

  const savePersonLink = async () => {
    if (!schoolId || !parentStudent || !editingLink?.relationshipId) return;
    setParentBusy(true);
    setPeopleSaving(true);
    setParentError("");
    try {
      const env = await put<{ success: boolean; data: PeopleLink[]; containerLabel?: string }>(
        `/api/schools/${encodeURIComponent(schoolId)}/person-links`,
        {
          studentUserId: parentStudent.userId,
          relationshipId: editingLink.relationshipId,
          kind: "guardian",
          guardianType,
          primary: linkPrimary,
          emergency: linkEmergency,
        },
      );
      setParentLinks(env.data || []);
      if (env.containerLabel) setPeopleLabel(env.containerLabel);
      setEditingLink(null);
      setContactOpen(false);
      resetLinkDraft();
    } catch (err) {
      setParentError(errorMessage(err));
    } finally {
      setParentBusy(false);
      setPeopleSaving(false);
    }
  };

  const openContactGrants = async (link: PeopleLink) => {
    if (!schoolId || !parentStudent || !link.userId) return;
    setContactGrantLink(link);
    setContactOpen(true);
    setContactLoading(true);
    setContactError("");
    setContactGrants(null);
    setContactDraft(null);
    try {
      const qs = new URLSearchParams({
        studentUserId: parentStudent.userId,
        userId: link.userId,
      });
      const env = await api<{ success: boolean; data: ContactGrants }>(
        `/api/schools/${encodeURIComponent(schoolId)}/person-links/contact-grants?${qs.toString()}`,
      );
      setContactGrants(env.data);
      setContactDraft({ student: env.data.student, guardian: env.data.guardian });
    } catch (err) {
      setContactError(errorMessage(err));
    } finally {
      setContactLoading(false);
    }
  };

  const saveContactGrants = async () => {
    if (!schoolId || !parentStudent || !contactGrantLink?.userId || !contactDraft || !contactGrants?.ugpId) return;
    setContactBusy(true);
    setContactError("");
    try {
      const env = await put<{ success: boolean; data: ContactGrants }>(
        `/api/schools/${encodeURIComponent(schoolId)}/person-links/contact-grants`,
        {
          studentUserId: parentStudent.userId,
          userId: contactGrantLink.userId,
          student: contactDraft.student,
          guardian: contactDraft.guardian,
        },
      );
      setContactGrants(env.data);
      setContactDraft({ student: env.data.student, guardian: env.data.guardian });
      setContactOpen(false);
      setContactGrantLink(null);
    } catch (err) {
      setContactError(errorMessage(err));
    } finally {
      setContactBusy(false);
    }
  };

  const unlinkPersonFromStudent = async (link: PeopleLink) => {
    if (!schoolId || !parentStudent) return;
    setParentBusy(true);
    setParentError("");
    try {
      const qs = new URLSearchParams({
        studentUserId: parentStudent.userId,
        kind: link.kind,
      });
      if (link.userId) qs.set("userId", link.userId);
      if (link.relationshipId) qs.set("relationshipId", link.relationshipId);
      await del(`/api/schools/${encodeURIComponent(schoolId)}/person-links?${qs.toString()}`);
      setParentLinks((current) =>
        current.filter(
          (row) =>
            !(
              row.kind === link.kind &&
              row.userId === link.userId &&
              row.relationshipId === link.relationshipId
            ),
        ),
      );
      await loadParticipants();
    } catch (err) {
      setParentError(errorMessage(err));
    } finally {
      setParentBusy(false);
    }
  };

  const openPersonContact = (userId: string, name: string) => {
    if (!schoolId || !userId) return;
    setPersonContact({ userId, name });
    setPersonContactRecord(null);
    setPersonContactError("");
    setPersonContactSaved("");
    setPersonContactLoading(true);
    setPersonContactValues({ email: "", phone: "", street: "", city: "", state: "", zip: "" });
    api<{
      success: boolean;
      data: {
        email: string;
        phone: string;
        canLogin: boolean;
        emailEditable?: boolean;
        phoneEditable?: boolean;
        street: string;
        city: string;
        state: string;
        zip: string;
      };
    }>(`/api/schools/${encodeURIComponent(schoolId)}/contacts/${encodeURIComponent(userId)}`)
      .then((env) => {
        if (!env.data) return;
        setPersonContactRecord(env.data);
        setPersonContactValues({
          email: env.data.email || "",
          phone: env.data.phone || "",
          street: env.data.street || "",
          city: env.data.city || "",
          state: env.data.state || "",
          zip: env.data.zip || "",
        });
      })
      .catch((err) => setPersonContactError(errorMessage(err)))
      .finally(() => setPersonContactLoading(false));
  };

  const grantPersonAccess = async (link: PeopleLink, confirm = false) => {
    if (!schoolId || !parentStudent || !link.userId || !link.email) return;
    setParentBusy(true);
    setPeopleSaving(true);
    setParentError("");
    try {
      const env = await post<{
        success: boolean;
        data?: PeopleLink[] | RoleKitPlan;
        containerLabel?: string;
      }>(`/api/schools/${encodeURIComponent(schoolId)}/person-links/access`, {
        studentUserId: parentStudent.userId,
        userId: link.userId,
        kind: link.kind,
        email: link.email,
        confirm,
      });
      if (isRoleKitPlan(env.data)) {
        setRolePlan(env.data);
        setKitAction({ kind: "grant", link });
        return;
      }
      setParentLinks(Array.isArray(env.data) ? env.data : []);
      if (env.containerLabel) setPeopleLabel(env.containerLabel);
      setRolePlan(null);
      setKitAction(null);
    } catch (err) {
      setParentError(errorMessage(err));
    } finally {
      setParentBusy(false);
      setPeopleSaving(false);
    }
  };

  const handleAdd = async (event: FormEvent, confirm = false) => {
    event.preventDefault();
    if (!schoolId) return;
    setSubmitting(true);
    try {
      const envelope = await post<{
        success: boolean;
        data?: RoleKitPlan & {
          kit?: { granted?: string[]; failed?: { id: string; message: string }[] };
        };
      }>(`/api/schools/${encodeURIComponent(schoolId)}/participants`, {
        firstName: newFirstName.trim(),
        lastName: newLastName.trim(),
        email: newEmail.trim(),
        role: newRole,
        confirm,
        ...(canOverrideAddPermissions && kitDraft ? { permissions: kitDraft } : {}),
      });
      if (envelope.data?.needsConfirmation) {
        setRolePlan(envelope.data);
        setKitAction({ kind: "add" });
        return;
      }
      const failed = envelope.data?.kit?.failed || [];
      if (failed.length) {
        notice({
          type: "warning",
          title: "Person added, some permissions did not grant",
          subtext: failed.map((row) => row.id).join(", "),
          duration: 8000,
        });
      } else {
        toast.success("Person added to school");
      }
      setNewFirstName("");
      setNewLastName("");
      setNewEmail("");
      setShowAdd(false);
      setShowKit(false);
      setRolePlan(null);
      setKitAction(null);
      await loadParticipants();
    } catch (err) {
      notice({
        type: "error",
        title: "Could not add person",
        subtext: errorMessage(err),
        duration: 8000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopyCourse = async (course: SchoolCourse) => {
    setCourseSaving(true);
    try {
      await post(`/api/courses/${encodeURIComponent(course.id)}/copy`, {});
      toast.success("Course copied");
      await loadCourses();
    } catch (err) {
      notice({
        type: "error",
        title: "Could not copy the course",
        subtext: errorMessage(err),
        duration: 7000,
      });
    } finally {
      setCourseSaving(false);
    }
  };

  const populatedRosterRoles = visibleRosterRoles.filter((opt) => (byRole[opt.key] || []).length > 0);
  const roleFilters = [
    ...populatedRosterRoles.map((opt) => ({
      key: opt.key,
      label: opt.label,
      count: (byRole[opt.key] || []).length,
    })),
    ...(schoolParents.length > 0 ? [{ key: "parent", label: "Parent", count: schoolParents.length }] : []),
  ];
  const rosterRows = [
    ...(rosterFilter === "parent"
      ? []
      : visibleRosterRoles
          .filter((opt) => rosterFilter === "all" || rosterFilter === opt.key)
          .flatMap((opt) =>
            (byRole[opt.key] || []).map((p) => ({
              id: `${p.role}:${p.userId}:${p.ugpId || ""}`,
              userId: p.userId || "",
              name: displayName(p),
              meta: [p.email, p.teamName, p.period_title].filter(Boolean).join(" · "),
              badge: p.roleLabel || opt.label,
              participant: p as Participant | null,
            })),
          )),
    ...(rosterFilter === "all" || rosterFilter === "parent"
      ? schoolParents.map((p) => ({
          id: `parent:${p.userId}`,
          userId: p.userId,
          name: p.name,
          meta: p.students.length ? `Parent of ${p.students.join(", ")}` : "Parent",
          badge: "Parent",
          participant: null as Participant | null,
        }))
      : []),
  ].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }) || a.badge.localeCompare(b.badge));
  const allCount =
    visibleRosterRoles.reduce((sum, opt) => sum + (byRole[opt.key] || []).length, 0) + schoolParents.length;
  const hasAny = participants.length > 0 || schoolParents.length > 0;
  const openAdd = () => {
    if (rosterFilter !== "all" && rosterFilter !== "parent" && allowedRoles.some((opt) => opt.key === rosterFilter)) {
      setNewRole(rosterFilter);
    }
    setShowAdd(true);
  };
  const skipSchoolList = Boolean(autoOpenSchoolId(session?.acuityLearn));
  const visibleLinks = parentLinks.filter((link) => link.kind === linkKind);
  const hasPrimaryGuardian = parentLinks.some((link) => link.kind === "guardian" && link.primary);
  const editingOtherPrimary = parentLinks.some(
    (link) =>
      link.kind === "guardian" &&
      link.primary &&
      link.relationshipId !== editingLink?.relationshipId,
  );
  const personContactDirty =
    Boolean(personContactRecord) &&
    (["email", "phone", "street", "city", "state", "zip"] as const).some(
      (id) => personContactValues[id] !== (personContactRecord?.[id] || ""),
    );
  const personContactLoginLocked =
    loginFieldLocked(personContactRecord, "email", capabilities) ||
    loginFieldLocked(personContactRecord, "phone", capabilities);
  const relationshipNote =
    parentStudent && !editingLink && !peopleAdding
      ? `These are the relationships for ${displayName(parentStudent)}.`
      : null;
  const relationshipDirty = Boolean(
    editingLink &&
      (guardianType !== (editingLink.guardianType || "other") ||
        linkPrimary !== Boolean(editingLink.primary) ||
        linkEmergency !== Boolean(editingLink.emergency)),
  );

  return (
    <div className="al-school-manage" ref={pageRef}>
      <LoadingOverlay isVisible={loading} message="Loading school…" />
      <LoadingOverlay isVisible={peopleSaving} message="Linking…" />
      {skipSchoolList ? null : (
        <Link to="/schools" className="al-school-manage-back">
          <ArrowLeft size={14} aria-hidden="true" />
          Schools
        </Link>
      )}

      {loading ? null : !school ? (
        <p className="al-schools-muted">School not found.</p>
      ) : (
        <>
          <header className="al-school-manage-header" id="al-school-details">
            <div className="al-school-manage-title-row">
              {canEdit && editingField === "name" ? (
                <input
                  className="al-school-manage-title-input"
                  value={name}
                  autoFocus
                  disabled={saving}
                  aria-label="School name"
                  onChange={(event) => setName(event.target.value)}
                  onBlur={() => void handleSaveSchool()}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      void handleSaveSchool();
                    }
                    if (event.key === "Escape") {
                      setName(school.name || "");
                      setEditingField(null);
                    }
                  }}
                />
              ) : (
                <h1
                  className={canEdit ? "al-school-manage-title is-editable" : "al-school-manage-title"}
                  title={canEdit ? "Double-click to rename" : undefined}
                  onDoubleClick={() => {
                    if (!canEdit || saving) return;
                    setName(school.name || "");
                    setEditingField("name");
                  }}
                >
                  {school.name}
                </h1>
              )}
              <button
                type="button"
                className="al-school-manage-info"
                aria-label="How this school page works"
                title="How this school page works"
                onClick={() => setShowHelp(true)}
              >
                <CircleHelp size={15} aria-hidden="true" />
              </button>
            </div>
            {canEdit && editingField === "description" ? (
              <textarea
                className="al-school-manage-desc-input"
                value={description}
                autoFocus
                rows={2}
                disabled={saving}
                aria-label="School description"
                onChange={(event) => setDescription(event.target.value)}
                onBlur={() => void handleSaveSchool()}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey) {
                    event.preventDefault();
                    void handleSaveSchool();
                  }
                  if (event.key === "Escape") {
                    setDescription(school.description || "");
                    setEditingField(null);
                  }
                }}
              />
            ) : (
              <p
                className={`al-school-manage-sub${canEdit ? " is-editable" : ""}${
                  school.description ? "" : " is-empty"
                }`}
                title={canEdit ? "Double-click to edit description" : undefined}
                onDoubleClick={() => {
                  if (!canEdit || saving) return;
                  setDescription(school.description || "");
                  setEditingField("description");
                }}
              >
                {school.description || (canEdit ? "Double-click to add a description" : "No description")}
              </p>
            )}
          </header>

          {canSeeCourses ? (
            <section className="al-school-manage-panel" id="al-school-courses">
              <div className="al-school-manage-panel-head">
                <h2 className="al-school-manage-panel-title">
                  <span className="al-school-manage-panel-icon" aria-hidden="true">
                    <BookOpen size={14} />
                  </span>
                  Courses at this school
                </h2>
              </div>

              {coursesLoading ? (
                <InlineLoading message="Loading courses…" />
              ) : courses.length === 0 ? (
                <p className="al-school-manage-hint">
                  This school has no courses yet.
                  {canSeeCatalog ? (
                    <>
                      {" "}
                      Courses are given out from the <Link to="/courses">Courses</Link> page.
                    </>
                  ) : (
                    " Ask someone who can assign courses to give this school a copy."
                  )}
                </p>
              ) : (
                <div className="al-school-courses-list">
                  {courses.map((course) => (
                    <div key={course.id} className="al-school-course">
                      <p className="al-school-course-title">{course.title}</p>
                      <div className="al-school-course-actions">
                        {canOpenBuilder && course.canBuild ? (
                          <Link
                            to={`/courses/${encodeURIComponent(course.id)}/build`}
                            className="al-school-course-action"
                            title="Open this school’s course copy"
                            aria-label={`Build ${course.title}`}
                          >
                            <Hammer size={13} aria-hidden="true" />
                            Build
                          </Link>
                        ) : null}
                        {canManageEnrollments && course.canEnroll !== false ? (
                          <button
                            type="button"
                            className="al-school-course-action"
                            onClick={() =>
                              schoolId
                                ? setEnrollTarget({
                                    courseId: course.id,
                                    title: course.title,
                                    schoolId,
                                    schoolName: school?.name,
                                  })
                                : undefined
                            }
                            title="Enroll people"
                            aria-label={`Enroll people in ${course.title}`}
                          >
                            <Users size={13} aria-hidden="true" />
                            Enroll
                          </button>
                        ) : null}
                        {canCopyCourses && course.canCopy !== false ? (
                          <button
                            type="button"
                            className="al-school-course-action"
                            disabled={courseSaving}
                            onClick={() => void handleCopyCourse(course)}
                            title="Copy this course for this school"
                            aria-label={`Copy ${course.title}`}
                          >
                            <Copy size={13} aria-hidden="true" />
                            Copy
                          </button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          ) : null}

          {/* Performance. Remove this section, SchoolPerformance, schoolPerformanceService, and the route to take it out. */}
          <section className="al-school-manage-panel" id="al-school-performance">
            <div className="al-school-manage-panel-head">
              <h2 className="al-school-manage-panel-title">
                <span className="al-school-manage-panel-icon" aria-hidden="true">
                  <BarChart3 size={14} />
                </span>
                Performance
              </h2>
            </div>
            <SchoolPerformance schoolId={school.id} />
          </section>

          {canSeePeople || canAdd ? (
          <section className="al-school-manage-panel" id="al-school-people" ref={peopleRef}>
            <div className="al-school-manage-panel-head">
              <h2 className="al-school-manage-panel-title">
                <span className="al-school-manage-panel-icon" aria-hidden="true">
                  <Users size={14} />
                </span>
                People at this school
              </h2>
              {canSeePeople && roleFilters.length > 1 ? (
                <div className="al-school-manage-filters" role="group" aria-label="Filter by role">
                  <button
                    type="button"
                    className={`al-school-manage-filter${rosterFilter === "all" ? " al-school-manage-filter--active" : ""}`}
                    onClick={() => setRosterFilter("all")}
                  >
                    All
                    <span className="al-school-manage-filter-count">{allCount}</span>
                  </button>
                  {roleFilters.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      className={`al-school-manage-filter${rosterFilter === opt.key ? " al-school-manage-filter--active" : ""}`}
                      onClick={() => setRosterFilter(opt.key)}
                    >
                      {opt.label}
                      <span className="al-school-manage-filter-count">{opt.count}</span>
                    </button>
                  ))}
                </div>
              ) : null}
            </div>

            {canSeePeople && visibleRosterRoles.length === 0 ? (
              <p className="al-school-manage-hint">
                school.ViewUsers is granted, but metadata.view_roles has no roles checked in Page Permissions Manager.
              </p>
            ) : canSeePeople && participantsLoading ? (
              <InlineLoading message="Loading people…" />
            ) : canSeePeople && !hasAny && !canAdd ? (
              <div className="al-school-manage-empty">
                <Users aria-hidden="true" />
                <h3>No people yet</h3>
                <p>Ask someone with school.AddUsers to add people.</p>
              </div>
            ) : canSeePeople || canAdd ? (
              <div className="al-school-manage-roster">
                {rosterFilter === "parent" && rosterRows.length === 0 ? (
                  <p className="al-school-manage-hint">
                    No parents linked yet.
                    {canLinkParents ? " Use Parents on a student to connect one." : ""}
                  </p>
                ) : null}
                <ul>
                  {rosterRows.map((row) => {
                    const person = row.participant;
                    return (
                    <li key={row.id} className="al-school-manage-person">
                      <div>
                        <div className="al-school-manage-person-line">
                          <span className="al-school-manage-person-name">{row.name}</span>
                          <span className="al-school-manage-role-badge">{row.badge}</span>
                        </div>
                        {row.meta ? <div className="al-school-manage-person-meta">{row.meta}</div> : null}
                      </div>
                      {person?.role === "student" && canViewPersonLinks || (canSeeContact && row.userId) ? (
                        <span className="al-people-row-actions">
                          {person?.role === "student" && canViewPersonLinks ? (
                            <SiteTip label="Relationships with people, such as guardians, counselors, and siblings.">
                              <button
                                type="button"
                                className="al-school-manage-people"
                                aria-label={`Relationships for ${row.name}`}
                                onClick={() => void openParents(person)}
                              >
                                <Users size={15} aria-hidden="true" />
                              </button>
                            </SiteTip>
                          ) : null}
                          {canSeeContact && row.userId ? (
                            <SiteTip label="Update email, phone, and address. Emergency contact is set on the relationship.">
                              <button
                                type="button"
                                className="al-school-manage-people"
                                aria-label={`Contact information for ${row.name}`}
                                onClick={() => openPersonContact(row.userId, row.name)}
                              >
                                <Contact size={15} aria-hidden="true" />
                              </button>
                            </SiteTip>
                          ) : null}
                        </span>
                      ) : null}
                    </li>
                    );
                  })}
                  {canAdd ? (
                    <li
                      ref={addPersonSlotRef}
                      className="al-school-manage-add-row"
                      style={pinAddPerson ? { height: pinAddPerson.height } : undefined}
                    >
                      <button
                        type="button"
                        className={pinAddPerson ? "is-float" : undefined}
                        style={floatRowStyle(pinAddPerson)}
                        onClick={openAdd}
                      >
                        <UserPlus size={15} aria-hidden="true" />
                        Add person
                      </button>
                    </li>
                  ) : null}
                </ul>
              </div>
            ) : null}
          </section>
          ) : null}

          {schoolId ? <SchoolAnnouncements schoolId={schoolId} canPost={canEdit} /> : null}

          {(canEditGradeScale || canEditCalendar) && schoolId ? (
            <SchoolYearPanel
              schoolId={schoolId}
              canEditScale={canEditGradeScale}
              canEditCalendar={canEditCalendar}
            />
          ) : null}
        </>
      )}

      <NotificationModal
        isOpen={Boolean(parentStudent) && canViewPersonLinks}
        type="neutral"
        icon={editingLink ? "none" : <Users />}
        title={editingLink ? editingLink.name : peopleLabel}
        message={null}
        overlay
        overlayClose={!parentBusy && !contactOpen && !relationshipHelp}
        escapeClose={!parentBusy && !contactOpen && !relationshipHelp}
        showOkButton={false}
        align="center"
        iconSize="1.35rem"
        titleSize={editingLink ? "1.05rem" : "1rem"}
        style={{
          ...ADD_PERSON_DIALOG_STYLE,
          width: editingLink
            ? "min(26rem, 100%)"
            : peopleAdding && linkKind === "guardian"
              ? "min(36rem, 100%)"
              : peopleAdding
                ? "min(22rem, 100%)"
                : ADD_PERSON_DIALOG_STYLE.width,
          maxHeight: "min(90vh, 48rem)",
        }}
        onClose={closeParents}
      >
        <div className={`al-people-sheet${peopleAdding || editingLink ? " is-adding" : ""}`}>
          <LoadingOverlay
            isVisible={parentLinksLoading || peopleSearching}
            message={peopleSearching ? "Searching…" : "Loading people…"}
            style={{ position: "absolute", inset: 0, zIndex: 3, borderRadius: "inherit" }}
          />
          {relationshipNote ? (
            <p className="al-people-rel-note">
              <span>{relationshipNote}</span>
              <button
                type="button"
                className="al-school-manage-info"
                aria-label="What these buttons do"
                onClick={() => setRelationshipHelp(true)}
              >
                <CircleHelp size={14} aria-hidden="true" />
              </button>
            </p>
          ) : null}
          {parentError ? <p className="al-school-manage-hint">{parentError}</p> : null}
          {!parentLinksLoading && !parentLinks.length && !peopleAdding && !editingLink ? (
            <p className="al-school-manage-hint">No one linked yet.</p>
          ) : null}
          {!peopleAdding && !editingLink && (parentLinks.length || !parentLinksLoading) ? (
          <div className="al-school-manage-roster">
            <ul>
              {parentLinks.map((link) => {
                const canEditRelationship = canLinkGuardians && link.kind === "guardian" && !parentBusy;
                const canGrantAccess = link.kind === "counselor" ? canGrantCounselorAccess : canGrantParentAccess;
                return (
                <li
                  key={`${link.kind}:${link.relationshipId || link.userId}`}
                  className={`al-school-manage-person${canEditRelationship ? " is-editable" : ""}`}
                  onClick={canEditRelationship ? () => openEditLink(link) : undefined}
                >
                  <div>
                    <div className="al-school-manage-person-line">
                      <span className="al-school-manage-person-name">{link.name}</span>
                      <span className="al-school-manage-role-badge">
                        {link.label}
                        {link.primary ? " · Primary" : ""}
                      </span>
                      {link.emergency ? <span className="al-school-manage-emergency-badge">Emergency</span> : null}
                      {!link.email || !link.platformAccess ? (
                        <SiteTip label={loginStatusHint(link, canGrantAccess, canEditContact)}>
                          <span
                            className="al-people-cannot-login"
                            tabIndex={0}
                            onClick={(event) => event.stopPropagation()}
                          >
                            {link.kind === "counselor" ? "Cannot login as counselor" : "Cannot login as parent"}
                          </span>
                        </SiteTip>
                      ) : null}
                    </div>
                  </div>
                  <span className="al-people-row-actions">
                    {canSeeContact && link.userId ? (
                      <SiteTip label="Update email, phone, and address. Emergency contact is set on the relationship.">
                        <button
                          type="button"
                          className="al-school-manage-parent-action"
                          aria-label={`Contact information for ${link.name}`}
                          disabled={parentBusy}
                          onClick={(event) => {
                            event.stopPropagation();
                            openPersonContact(link.userId || "", link.name);
                          }}
                        >
                          <Contact size={14} aria-hidden="true" />
                        </button>
                      </SiteTip>
                    ) : null}
                    {canGrantContact && link.kind === "guardian" && link.userId ? (
                      <SiteTip label="Set this guardian’s contact permissions for the student and for other guardians.">
                        <button
                          type="button"
                          className="al-school-manage-parent-action"
                          aria-label={`Contact permissions for ${link.name}`}
                          disabled={parentBusy}
                          onClick={(event) => {
                            event.stopPropagation();
                            void openContactGrants(link);
                          }}
                        >
                          <Settings size={14} aria-hidden="true" />
                        </button>
                      </SiteTip>
                    ) : null}
                    {canLinkGuardians && link.kind === "guardian" ? (
                      <button
                        type="button"
                        className="al-school-manage-parent-action"
                        aria-label={`Edit ${link.name}`}
                        title="Edit relationship"
                        disabled={parentBusy}
                        onClick={(event) => {
                          event.stopPropagation();
                          openEditLink(link);
                        }}
                      >
                        <SquarePen size={14} aria-hidden="true" />
                      </button>
                    ) : null}
                    {link.email && !link.platformAccess && link.userId && canGrantAccess ? (
                      <button
                        type="button"
                        className="al-school-manage-parent-action"
                        aria-label={`Give access to ${link.name}`}
                        title="Give access"
                        disabled={parentBusy}
                        onClick={(event) => {
                          event.stopPropagation();
                          void grantPersonAccess(link);
                        }}
                      >
                        <ShieldCheck size={14} aria-hidden="true" />
                      </button>
                    ) : null}
                    {canLinkParents ? (
                    <button
                      type="button"
                      className="al-school-manage-parent-action al-school-manage-parent-action--remove"
                      aria-label={`Double-click to remove ${link.name}`}
                      title="Double-click to remove"
                      disabled={parentBusy}
                      onClick={(event) => event.stopPropagation()}
                      onDoubleClick={(event) => {
                        event.stopPropagation();
                        void unlinkPersonFromStudent(link);
                      }}
                    >
                      <X size={11} aria-hidden="true" />
                    </button>
                    ) : null}
                  </span>
                </li>
                );
              })}
              {!parentLinksLoading && !peopleAdding && canLinkParents ? (
                <li className="al-school-manage-add-row">
                  <button
                    type="button"
                    disabled={parentBusy}
                    onClick={() => {
                      setEditingLink(null);
                      setLinkKind(canLinkGuardians ? "guardian" : "counselor");
                      resetLinkDraft();
                      setPeopleAdding(true);
                    }}
                  >
                    <UserPlus size={15} aria-hidden="true" />
                    Add a Relationship
                  </button>
                </li>
              ) : null}
            </ul>
          </div>
          ) : null}
          {editingLink ? (
            <div className="al-people-add">
              <div className="al-people-add-head">
                <button
                  type="button"
                  className="al-people-back"
                  aria-label="Back to people"
                  disabled={parentBusy}
                  onClick={() => {
                    setEditingLink(null);
                    setContactOpen(false);
                    resetLinkDraft();
                  }}
                >
                  <ArrowLeft size={14} aria-hidden="true" />
                </button>
                <span>Relationship</span>
                <span />
              </div>
              <div className="al-people-edit">
                <label className="al-account-contact-field">
                  <span className="al-account-contact-label">Type</span>
                  <select
                    className="al-account-contact-input"
                    aria-label="Guardian type"
                    value={guardianType}
                    disabled={parentBusy}
                    onChange={(event) => setGuardianType(event.target.value)}
                  >
                    {GUARDIAN_TYPE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                </label>
                <div className="al-people-edit-checks">
                  <label className="al-school-manage-check">
                    <input
                      type="checkbox"
                      checked={linkPrimary}
                      disabled={parentBusy || editingOtherPrimary}
                      onChange={(event) => setLinkPrimary(event.target.checked)}
                    />
                    Primary{editingOtherPrimary ? " already set" : ""}
                  </label>
                  <label className="al-school-manage-check">
                    <input
                      type="checkbox"
                      checked={linkEmergency}
                      disabled={parentBusy}
                      onChange={(event) => setLinkEmergency(event.target.checked)}
                    />
                    Emergency contact
                  </label>
                </div>
              </div>
              {relationshipDirty ? (
                <button
                  type="button"
                  className="al-account-contact-save"
                  disabled={parentBusy}
                  onClick={() => void savePersonLink()}
                >
                  {parentBusy ? "Saving" : "Save"}
                </button>
              ) : null}
            </div>
          ) : null}
          {peopleAdding ? (
            <div className="al-people-add">
              <div className="al-people-add-head">
                <button
                  type="button"
                  className="al-people-back"
                  aria-label="Back to people"
                  disabled={parentBusy}
                  onClick={() => setPeopleAdding(false)}
                >
                  <ArrowLeft size={14} aria-hidden="true" />
                </button>
                <span>Add a Relationship</span>
              </div>
              <div className="profile-pl-rel-cards" role="radiogroup" aria-label="Relationship type">
                {canLinkGuardians ? (
                <button
                  type="button"
                  role="radio"
                  aria-checked={linkKind === "guardian"}
                  className={`profile-pl-rel-card${linkKind === "guardian" ? " is-selected" : ""}`}
                  disabled={parentBusy}
                  onClick={() => {
                    setLinkKind("guardian");
                    resetLinkDraft();
                  }}
                >
                  <span className="profile-pl-rel-card-head">
                    <span className="profile-pl-rel-card-label">Guardian</span>
                    <span className="profile-pl-rel-card-shape">All periods</span>
                  </span>
                </button>
                ) : null}
                {canLinkCounselors ? (
                <button
                  type="button"
                  role="radio"
                  aria-checked={linkKind === "counselor"}
                  className={`profile-pl-rel-card${linkKind === "counselor" ? " is-selected" : ""}`}
                  disabled={parentBusy}
                  onClick={() => {
                    setLinkKind("counselor");
                    resetLinkDraft();
                  }}
                >
                  <span className="profile-pl-rel-card-head">
                    <span className="profile-pl-rel-card-label">Counselor</span>
                    <span className="profile-pl-rel-card-shape">This period</span>
                  </span>
                </button>
                ) : null}
              </div>
              <div className="al-people-finder">
                {linkKind === "guardian" && canSearchPeople ? (
                  <div className="profile-sheet-toggle" role="group" aria-label="How to add a guardian">
                    <button
                      type="button"
                      className={`profile-sheet-toggle-btn${linkMode === "search" ? " is-active" : ""}`}
                      disabled={parentBusy}
                      onClick={() => setLinkMode("search")}
                    >
                      Search existing
                    </button>
                    <button
                      type="button"
                      className={`profile-sheet-toggle-btn${linkMode === "create" ? " is-active" : ""}`}
                      disabled={parentBusy}
                      onClick={() => {
                        setLinkMode("create");
                        setParentQuery("");
                        setParentHits([]);
                        setParentSearched(false);
                        setSelectedHit(null);
                      }}
                    >
                      Create new
                    </button>
                  </div>
                ) : null}
                <div className="al-people-finder-body">
                  {linkKind === "counselor" || (canSearchPeople && linkMode === "search") ? (
                    <form className="profile-sheet-search-row" onSubmit={(event) => void searchParents(event)}>
                      <input
                        className="profile-sheet-input"
                        value={parentQuery}
                        onChange={(event) => setParentQuery(event.target.value)}
                        placeholder="Name or email"
                        disabled={parentBusy}
                        aria-label={linkKind === "guardian" ? "Find a guardian" : "Find a counselor"}
                      />
                      <button
                        type="submit"
                        className={`profile-sheet-search-submit${parentQuery.trim() ? " is-ready" : ""}`}
                        disabled={parentBusy || !parentQuery.trim()}
                      >
                        Go
                      </button>
                    </form>
                  ) : (
                    <div className="al-people-create">
                      <input
                        className="profile-sheet-input"
                        value={createFirst}
                        onChange={(event) => setCreateFirst(event.target.value)}
                        placeholder="First name"
                        disabled={parentBusy}
                        aria-label="First name"
                      />
                      <input
                        className="profile-sheet-input"
                        value={createLast}
                        onChange={(event) => setCreateLast(event.target.value)}
                        placeholder="Last name"
                        disabled={parentBusy}
                        aria-label="Last name"
                      />
                      <input
                        className="profile-sheet-input"
                        type="email"
                        value={createEmail}
                        onChange={(event) => setCreateEmail(event.target.value)}
                        placeholder="Email"
                        disabled={parentBusy}
                        aria-label="Email"
                      />
                    </div>
                  )}
                  {parentSearched && !parentHits.length && !parentBusy ? (
                    <p className="profile-sheet-hint">No matches.</p>
                  ) : null}
                  {(linkKind === "counselor" || (canSearchPeople && linkMode === "search")) && parentHits.length ? (
                    <div className="profile-sheet-results">
                      {parentHits.map((hit) => {
                        const name = [hit.first_name, hit.last_name].filter(Boolean).join(" ").trim() || hit.email || "Unnamed";
                        const already = visibleLinks.some((link) => link.userId === String(hit.id));
                        const detail = hit.seat || hit.email || (hit.can_login === false ? "No login" : "");
                        const selected = selectedHit?.ugpId
                          ? selectedHit.ugpId === hit.ugpId
                          : selectedHit?.id === hit.id;
                        return (
                          <button
                            key={hit.ugpId || hit.id}
                            type="button"
                            className={`profile-sheet-result-item${selected ? " is-selected" : ""}`}
                            disabled={parentBusy || already || String(hit.id) === String(parentStudent?.userId)}
                            onClick={() => setSelectedHit(hit)}
                          >
                            {name}
                            {detail ? <span>{detail}</span> : null}
                          </button>
                        );
                      })}
                    </div>
                  ) : null}
                </div>
              <div className="al-people-link-bar">
                {linkKind === "guardian" ? (
                  <>
                    <select
                      className="profile-sheet-input"
                      aria-label="Guardian type"
                      value={guardianType}
                      disabled={parentBusy}
                      onChange={(event) => setGuardianType(event.target.value)}
                    >
                      {GUARDIAN_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <label className="al-school-manage-check">
                      <input
                        type="checkbox"
                        checked={linkPrimary}
                        disabled={parentBusy || hasPrimaryGuardian}
                        onChange={(event) => setLinkPrimary(event.target.checked)}
                      />
                      Primary{hasPrimaryGuardian ? " already set" : ""}
                    </label>
                    <label className="al-school-manage-check">
                      <input
                        type="checkbox"
                        checked={linkEmergency}
                        disabled={parentBusy}
                        onChange={(event) => setLinkEmergency(event.target.checked)}
                      />
                      Emergency contact
                    </label>
                  </>
                ) : null}
                <button
                  type="button"
                  className="al-people-link-btn"
                  disabled={
                    parentBusy ||
                    (linkMode === "create" && linkKind === "guardian"
                      ? !createFirst.trim() || !createLast.trim()
                      : !selectedHit)
                  }
                  onClick={() => void linkPersonToStudent(linkMode === "create" && linkKind === "guardian" ? undefined : selectedHit || undefined)}
                >
                  Link
                </button>
              </div>
              </div>
              <p className="profile-pl-rel-card-sub">
                {linkKind === "guardian"
                  ? "All periods — stays with this student in every school period. A name is enough — no login required."
                  : "This period — linked for this organization and school period. Pick someone who already has a seat here."}
              </p>
            </div>
          ) : null}
        </div>
      </NotificationModal>

      <NotificationModal
        isOpen={contactOpen && canGrantContact}
        type="neutral"
        icon={<Settings />}
        title="Contact permissions"
        message={contactGrantLink ? contactGrantLink.name : null}
        messageSize="0.72rem"
        overlay
        overlayClose={!contactBusy}
        escapeClose={!contactBusy}
        showOkButton={false}
        align="center"
        iconSize="1.35rem"
        titleSize="1.05rem"
        zIndex={1300}
        style={CONTACT_GRANT_DIALOG_STYLE}
        onClose={() => {
          if (contactBusy) return;
          setContactOpen(false);
          setContactGrantLink(null);
        }}
      >
        {contactLoading ? (
          <InlineLoading message="Loading contact permissions…" />
        ) : contactGrants && !contactGrants.ugpId ? (
          <p className="al-school-manage-hint al-contact-grants__lead">
            Give this person access first. Contact permissions sit on their parent membership.
          </p>
        ) : contactGrants && contactDraft ? (
          <div className="al-contact-grants">
            <p className="al-school-manage-hint al-contact-grants__lead">
              Saved on their parent membership. Choosing update includes view.
            </p>
            <ContactDoor
              title="This student's contact"
              detail="See or change this student's email, phone, and address."
              value={contactDraft.student}
              team={contactGrants.team.student}
              disabled={contactBusy}
              onChange={(student) => setContactDraft((prev) => (prev ? { ...prev, student } : prev))}
            />
            <ContactDoor
              title="Other guardians' contact"
              detail="See or change contact for the other guardians linked to this student."
              value={contactDraft.guardian}
              team={contactGrants.team.guardian}
              disabled={contactBusy}
              onChange={(guardian) => setContactDraft((prev) => (prev ? { ...prev, guardian } : prev))}
            />
          </div>
        ) : null}
        {contactError ? (
          <p className="al-school-manage-hint al-contact-grants__lead" role="alert">
            {contactError}
          </p>
        ) : null}
        {contactGrants?.ugpId && contactDraft ? (
          <div className="al-schools-form-actions">
            <button
              type="button"
              className="al-schools-btn al-schools-btn--ghost"
              disabled={contactBusy}
              onClick={() => {
                setContactOpen(false);
                setContactGrantLink(null);
              }}
            >
              Cancel
            </button>
            <button
              type="button"
              className="al-schools-btn"
              disabled={
                contactBusy ||
                (contactDraft.student === contactGrants.student &&
                  contactDraft.guardian === contactGrants.guardian)
              }
              onClick={() => void saveContactGrants()}
            >
              {contactBusy ? "Saving…" : "Save"}
            </button>
          </div>
        ) : null}
      </NotificationModal>

      <NotificationModal
        isOpen={showAdd && canAdd}
        type="neutral"
        icon={<UserPlus />}
        title="Add person"
        message={school?.name || null}
        overlay
        overlayClose={!submitting}
        escapeClose={!submitting}
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        style={ADD_PERSON_DIALOG_STYLE}
        onClose={() => {
          if (!submitting) {
            setShowAdd(false);
            setShowKit(false);
          }
        }}
      >
        {allowedRoles.length === 0 ? (
          <p className="al-school-manage-hint">
            school.AddUsers is granted, but metadata.add_roles has no roles checked in Page Permissions Manager.
          </p>
        ) : (
          <form className="al-school-manage-form" onSubmit={(event) => void handleAdd(event)}>
            <div className="al-school-manage-row">
              <div>
                <label className="al-schools-label" htmlFor="add-first">
                  First name *
                </label>
                <input
                  id="add-first"
                  className="al-schools-input"
                  value={newFirstName}
                  onChange={(event) => setNewFirstName(event.target.value)}
                  required
                  disabled={submitting}
                  autoFocus
                />
              </div>
              <div>
                <label className="al-schools-label" htmlFor="add-last">
                  Last name *
                </label>
                <input
                  id="add-last"
                  className="al-schools-input"
                  value={newLastName}
                  onChange={(event) => setNewLastName(event.target.value)}
                  required
                  disabled={submitting}
                />
              </div>
            </div>
            <label className="al-schools-label" htmlFor="add-email">
              Email *
            </label>
            <input
              id="add-email"
              className="al-schools-input"
              type="email"
              value={newEmail}
              onChange={(event) => setNewEmail(event.target.value)}
              required
              disabled={submitting}
            />
            <div className="al-school-role-field">
              <span className="al-schools-label" id="add-role-label">
                Role *
              </span>
              <div className="al-school-role-row" role="group" aria-labelledby="add-role-label">
                {allowedRoles.map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    className={`al-school-role-badge${newRole === opt.key ? " is-on" : ""}`}
                    aria-pressed={newRole === opt.key}
                    disabled={submitting || kitLoading}
                    onClick={() => setNewRole(opt.key)}
                  >
                    {opt.label}
                  </button>
                ))}
                {canOverrideAddPermissions ? (
                  <button
                    type="button"
                    className={`al-school-role-gear${showKit ? " is-on" : ""}`}
                    aria-label="Edit permissions for this person"
                    title="Permissions for this person"
                    disabled={submitting || kitLoading || !kitDraft}
                    onClick={() => setShowKit(true)}
                  >
                    <Settings size={14} strokeWidth={2} aria-hidden="true" />
                  </button>
                ) : null}
              </div>
              <p className="al-school-manage-hint">
                {kitLoading
                  ? "Loading role defaults…"
                  : kit
                    ? canOverrideAddPermissions
                      ? `${kit.source === "saved" ? "Saved" : "Built-in"} ${kit.label} kit. Gear lets you uncheck or add keys for this person only.`
                      : `${kit.source === "saved" ? "Saved" : "Built-in"} ${kit.label} kit will be stamped as-is.`
                    : canOverrideAddPermissions
                      ? "This person still gets the role badge. Open the gear after defaults load to review pages and capabilities."
                      : "This person still gets the role badge and the Role Defaults kit for that role."}
              </p>
            </div>
            <p className="al-school-manage-hint">
              If this email already exists in UIS, we connect that account. Otherwise UIS creates a login-capable
              user (they set a password with forgot-password). We do not delete UGPs from this screen.
            </p>
            <div className="al-schools-form-actions">
              <button
                type="button"
                className="al-schools-btn al-schools-btn--ghost"
                disabled={submitting}
                onClick={() => {
                  setShowAdd(false);
                  setShowKit(false);
                }}
              >
                Cancel
              </button>
              <button type="submit" className="al-schools-btn" disabled={submitting || kitLoading}>
                {submitting ? "Adding…" : kitLoading ? "Loading kit…" : "Add to school"}
              </button>
            </div>
          </form>
        )}
      </NotificationModal>

      <NotificationModal
        isOpen={showKit && canOverrideAddPermissions && Boolean(kit && kitDraft)}
        type="neutral"
        icon={<Settings />}
        title={kit ? `${kit.label} permissions` : "Permissions"}
        message={
          kit
            ? `Starting from the ${kit.source === "saved" ? "saved" : "built-in"} Role Defaults kit. The next step shows whether these go on the group or only on this person.`
            : null
        }
        overlay
        overlayClose={!submitting}
        escapeClose={!submitting}
        showOkButton={false}
        align="start"
        iconSize="2.25rem"
        titleSize="1.05rem"
        zIndex={1200}
        style={ADD_KIT_DIALOG_STYLE}
        onClose={() => {
          if (!submitting) setShowKit(false);
        }}
      >
        {kit && kitDraft ? (
          <>
            <PermissionKitEditor
              catalog={kit.catalog}
              value={kitDraft}
              onChange={setKitDraft}
              query={kitQuery}
              onQueryChange={setKitQuery}
            />
            <div className="al-schools-form-actions">
              <button
                type="button"
                className="al-schools-btn al-schools-btn--ghost"
                disabled={submitting || fingerprint(kitDraft) === fingerprint(kit.permissions)}
                onClick={() => {
                  setKitDraft(clonePermissions(kit.permissions));
                  setKitQuery("");
                }}
              >
                Reset to defaults
              </button>
              <button type="button" className="al-schools-btn" disabled={submitting} onClick={() => setShowKit(false)}>
                Done
              </button>
            </div>
          </>
        ) : null}
      </NotificationModal>

      <NotificationModal
        isOpen={Boolean(rolePlan)}
        type="neutral"
        icon="none"
        title={rolePlan?.teamName ? `${rolePlan.teamName} group` : "Group permissions"}
        message={rolePlan?.message || null}
        overlay
        overlayClose={!submitting && !parentBusy}
        escapeClose={!submitting && !parentBusy}
        showOkButton={false}
        showCloseButton={false}
        align="start"
        titleSize="1.05rem"
        messageSize="0.84rem"
        zIndex={1400}
        style={ADD_KIT_DIALOG_STYLE}
        onClose={() => {
          if (!submitting && !parentBusy) {
            setRolePlan(null);
            setKitAction(null);
          }
        }}
      >
        {rolePlan ? (
          <>
            <KitPlanList
              title={`On the ${rolePlan.teamName} group`}
              hint={kitPlanHints(rolePlan).gp}
              rows={rolePlan.gp}
            />
            <KitPlanList
              title="Only this person"
              hint={kitPlanHints(rolePlan).allow}
              rows={rolePlan.ugpAllow}
            />
            <KitPlanList
              title="This person will not receive"
              hint={kitPlanHints(rolePlan).deny}
              rows={rolePlan.ugpDeny}
            />
            <div className="al-schools-form-actions">
              <button
                type="button"
                className="al-schools-btn al-schools-btn--ghost"
                disabled={submitting || parentBusy}
                onClick={() => {
                  setRolePlan(null);
                  setKitAction(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="al-schools-btn"
                disabled={submitting || parentBusy}
                onClick={(event) => {
                  if (kitAction?.kind === "link") void linkPersonToStudent(kitAction.hit, true);
                  else if (kitAction?.kind === "grant") void grantPersonAccess(kitAction.link, true);
                  else void handleAdd(event, true);
                }}
              >
                {submitting || parentBusy ? "Adding…" : "OK"}
              </button>
            </div>
          </>
        ) : null}
      </NotificationModal>

      <NotificationModal
        isOpen={relationshipHelp}
        type="info"
        icon={<CircleHelp />}
        title="Relationship buttons"
        message="If a button is missing from a row, this membership does not have the access that shows it. Give access also stays hidden when that person already has a sign-in, or has no email."
        messageSize="0.72rem"
        overlay
        overlayClose
        showOkButton={false}
        align="center"
        iconSize="1.35rem"
        titleSize="1.05rem"
        zIndex={1200}
        style={{ width: "min(26rem, 100%)", maxHeight: "min(90vh, 40rem)" }}
        onClose={() => setRelationshipHelp(false)}
      >
        <RelationshipButtonHelp />
      </NotificationModal>

      <NotificationModal
        isOpen={showHelp}
        type="info"
        title="This school"
        message={null}
        overlay
        overlayClose
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        style={{ width: "min(24rem, 100%)", maxHeight: "min(90vh, 48rem)" }}
        onClose={() => setShowHelp(false)}
      >
        <dl className="al-school-help">
          <div>
            <dt>Details</dt>
            <dd>
              The name and description at the top of this page.
              {canEdit
                ? " Double-click the title to rename the school, or the line under it to edit the description."
                : " You can read them here."}
            </dd>
          </div>
          {canSeeCourses ? (
            <div>
              <dt>Courses</dt>
              <dd>
                Courses this school has been given. Build opens the school copy; Enroll and Copy appear when your
                permissions allow. New courses are given out from the Courses page.
              </dd>
            </div>
          ) : null}
          {canSeePeople || canAdd ? (
            <div>
              <dt>Participants</dt>
              <dd>
                {canSeePeople
                  ? "People at this school, listed by name. Each row has a role badge. When more than one role is present, the heading filters that list."
                  : "People at this school."}
                {canAdd
                  ? canOverrideAddPermissions
                    ? " Add person puts someone on a role team and stamps that role's default Learn permissions. The same person can be on more than one team (teacher and student are separate memberships). The gear on the role badge can change keys for that membership only."
                    : " Add person puts someone on a role team and stamps that role's default Learn permissions. The same person can be on more than one team (teacher and student are separate memberships)."
                  : ""}
                {canLinkParents
                  ? " Parents on a student links or removes a parent account for that student."
                  : ""}
                {canGrantContact
                  ? " The gear on a guardian row sets that guardian's contact permissions for this school's scope."
                  : ""}
              </dd>
            </div>
          ) : null}
          {canEditGradeScale ? (
            <div>
              <dt>Grade scale</dt>
              <dd>
                Letters for every class at this school. Double-click a number to change it. Save appears when a
                number changes.
              </dd>
            </div>
          ) : null}
          {canEditCalendar ? (
            <div>
              <dt>School calendar</dt>
              <dd>
                Holidays, days off, early release, and testing days. Add day opens a form for the date, school day,
                title, and note. The list stays short until you expand it.
              </dd>
            </div>
          ) : null}
        </dl>
        <p className="al-school-help-dev">
          Developer: adding a person creates or reuses a UIS user, puts them on the matching role team (GP + UGP),
          grants platform_access plus that role badge on the team, and stamps the Role Defaults kit onto the UGP.
        </p>
      </NotificationModal>

      <NotificationModal
        isOpen={Boolean(personContact)}
        type="neutral"
        icon="none"
        title={personContact?.name || "Contact information"}
        message={null}
        overlay
        overlayClose={!personContactSaving}
        escapeClose={!personContactSaving}
        showOkButton={false}
        align="center"
        titleSize="1.05rem"
        style={CONTACT_DIALOG_STYLE}
        onClose={() => {
          if (personContactSaving) return;
          setPersonContact(null);
        }}
      >
        <form
          id="al-school-contact"
          className="al-account-contact"
          onSubmit={(event) => {
            event.preventDefault();
            if (
              !schoolId ||
              !personContact ||
              !canEditContact ||
              !personContactRecord ||
              personContactSaving ||
              !personContactDirty
            ) {
              return;
            }
            setPersonContactSaving(true);
            setPersonContactError("");
            setPersonContactSaved("");
            put<{
              success: boolean;
              data: {
                email: string;
                phone: string;
                canLogin: boolean;
                emailEditable?: boolean;
                phoneEditable?: boolean;
                street: string;
                city: string;
                state: string;
                zip: string;
              };
            }>(
              `/api/schools/${encodeURIComponent(schoolId)}/contacts/${encodeURIComponent(personContact.userId)}`,
              personContactValues,
            )
              .then((env) => {
                if (!env.data) return;
                setPersonContactRecord(env.data);
                setPersonContactValues({
                  email: env.data.email || "",
                  phone: env.data.phone || "",
                  street: env.data.street || "",
                  city: env.data.city || "",
                  state: env.data.state || "",
                  zip: env.data.zip || "",
                });
                setPersonContactSaved("Saved.");
              })
              .catch((err) => setPersonContactError(errorMessage(err)))
              .finally(() => setPersonContactSaving(false));
          }}
        >
          <p className="al-account-contact-title">Contact information</p>
          {personContactError ? <p className="al-account-contact-note">{personContactError}</p> : null}
          {personContactLoading || !personContactRecord ? (
            personContactLoading ? (
              <div className="al-account-contact-loading" role="status" aria-label="Loading contact">
                <span className="al-account-contact-spinner" aria-hidden="true" />
              </div>
            ) : null
          ) : (
            <>
              <ContactFields
                values={personContactValues}
                idPrefix="al-school-contact-field"
                disabled={!canEditContact || personContactSaving}
                locked={(id) => loginFieldLocked(personContactRecord, id, capabilities)}
                onChange={(id, value) => {
                  setPersonContactSaved("");
                  setPersonContactValues((current) => ({ ...current, [id]: value }));
                }}
              />
              {personContactLoginLocked ? (
                <p className="al-account-contact-note">
                  {personContactRecord?.loginLock === "owner"
                    ? "The platform owner’s email and phone can only be changed by that person."
                    : personContactRecord?.loginLock === "scope"
                      ? "This person has a context outside your school scope. A filled email or phone needs Edit User Data Master."
                      : "A sign-in email or phone that already has a value stays as it is."}
                </p>
              ) : null}
              <p className="al-account-contact-note">
                {canEditContact
                  ? "Emergency contact is changed from the relationship, not here."
                  : "You can view this contact, not change it."}
              </p>
              {personContactSaved ? <p className="al-account-contact-note">{personContactSaved}</p> : null}
            </>
          )}
        </form>
        {canEditContact && personContactDirty ? (
          <button
            type="submit"
            form="al-school-contact"
            className="al-account-contact-save"
            disabled={personContactSaving}
          >
            {personContactSaving ? "Saving" : "Save"}
          </button>
        ) : null}
      </NotificationModal>

      <CourseEnrollDialog target={enrollTarget} onClose={() => setEnrollTarget(null)} />
    </div>
  );
}
